/* Live slides the panel with jQuery's `slideToggle(300)` and flips `.active` on the item
   at the same moment. `<details>` cannot animate its own height, so the panel is animated
   here instead and the open class is driven at click time rather than off `[open]`, which
   lags by the length of the close animation.

   Progressive enhancement: without this script the `<details>` still opens and closes, it
   just does so instantly — which is why the stylesheets carry an `[open]` rule as well.
   The root is flagged on connect so they know which of the two is in charge. */
if (!customElements.get('brookfarm-accordion')) {
  customElements.define(
    'brookfarm-accordion',
    class BrookfarmAccordion extends HTMLElement {
      connectedCallback() {
        this.duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300;
        this.setAttribute('data-bf-accordion-animated', '');

        this.querySelectorAll('details').forEach((details) => {
          const summary = details.querySelector('summary');
          const panel = details.querySelector('[data-bf-accordion-panel]');
          if (!summary || !panel) return;

          /* Anything already open server-side needs the class to match. */
          details.classList.toggle('bf-accordion__item--open', details.open);
          details.classList.toggle('bf-accordions__item--open', details.open);

          summary.addEventListener('click', (event) => {
            event.preventDefault();
            if (details.open) this.close(details, panel);
            else this.open(details, panel);
          });
        });
      }

      /* The open/closed state is deliberately NOT gated on the animation finishing. A
         backgrounded or throttled tab stops ticking animations entirely — `currentTime`
         never advances and `onfinish` never fires — which would strand a panel that had
         been clicked shut in a state where it could not be reopened. So the timer is the
         guarantee and `onfinish` is only the fast path; whichever lands first wins, and
         `settled` keeps the other from running twice. */
      animate(panel, from, to, onDone) {
        panel.style.overflow = 'hidden';
        const animation = panel.animate({ height: [from, to] }, { duration: this.duration, easing: 'ease' });

        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          panel.bfFinish = null;
          animation.cancel();
          panel.style.overflow = '';
          panel.style.height = '';
          if (onDone) onDone();
        };

        const timer = setTimeout(finish, this.duration + 50);
        animation.onfinish = finish;
        /* Settling the previous slide is the caller's job, before it mutates any state —
           otherwise a close interrupted mid-slide would run its "now set open = false"
           after the new open had already set it true. */
        panel.bfFinish = finish;
      }

      open(details, panel) {
        /* Siblings first: live slides them shut over the same 300ms, so both moves run
           together rather than one waiting on the other. */
        if (this.hasAttribute('single')) {
          this.querySelectorAll('details[open]').forEach((other) => {
            if (other === details) return;
            const otherPanel = other.querySelector('[data-bf-accordion-panel]');
            if (otherPanel) this.close(other, otherPanel);
          });
        }

        panel.bfFinish?.();
        details.open = true;
        details.classList.add('bf-accordion__item--open');
        details.classList.add('bf-accordions__item--open');
        this.animate(panel, '0px', `${panel.scrollHeight}px`);
      }

      close(details, panel) {
        panel.bfFinish?.();
        details.classList.remove('bf-accordion__item--open');
        details.classList.remove('bf-accordions__item--open');
        const start = `${panel.getBoundingClientRect().height}px`;
        this.animate(panel, start, '0px', () => {
          details.open = false;
        });
      }
    }
  );
}
